import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Link } from '@inertiajs/react'
import { Bot, User, Eye, EyeOff } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import DraftEditor from './DraftEditor'
import AttachmentPicker from './AttachmentPicker'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'
import { formatShortDateTime } from '@/lib/formatters'
import { capitalize } from '@/lib/utils'

interface Attachment {
  id: string
  filename: string
  mime_type: string
  size: number
}

interface Draft {
  id: string
  to: string
  cc: string | null
  bcc: string | null
  subject: string
  body: string
  body_html: string | null
  original_body: string | null
  status: string
  sent_at: string | null
  sent_by_id: string | null
  created_at: string
  created_by_id: string | null
  created_by_name: string | null
  agent_session_id: string | null
  send_error: string | null
  attachments?: Attachment[]
}

interface DraftCardProps {
  draft: Draft
  filter?: string
  attention?: string | null
  search?: string
  cursor?: string | null
}

export default function DraftCard({ draft, filter, attention, search, cursor }: DraftCardProps) {
  const [showOriginal, setShowOriginal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  // Attachments are managed locally because AttachmentPicker uploads/removes via XHR,
  // but resynced from props when Inertia refreshes the page with new data.
  const [attachments, setAttachments] = useState<Attachment[]>(draft.attachments || [])
  const [attachmentSyncKey, setAttachmentSyncKey] = useState(draft.id + (draft.attachments?.length ?? 0))
  const pathFor = useOrgPath()

  // Resync attachments when props change (Inertia re-render)
  const newSyncKey = draft.id + (draft.attachments?.length ?? 0)
  if (newSyncKey !== attachmentSyncKey) {
    setAttachments(draft.attachments || [])
    setAttachmentSyncKey(newSyncKey)
  }

  const isAIDraft = draft.agent_session_id != null
  const isEdited = draft.original_body != null
  const displayBody = showOriginal ? (draft.original_body || draft.body) : draft.body

  // Determine available actions based on status
  const isDraft = draft.status === 'draft'
  const isSendFailed = draft.status === 'send_failed'
  const canSend = isDraft || isSendFailed
  const canDiscard = isDraft || isSendFailed
  const canEdit = isDraft

  // Build query params to preserve navigation state
  const queryParams: Record<string, string> = {}
  if (filter) queryParams.filter = filter
  if (attention) queryParams.attention = attention
  if (search) queryParams.search = search
  if (cursor) queryParams.cursor = cursor

  function handleSend() {
    router.post(pathFor(`/emails/${draft.id}/send`), queryParams, {
      preserveScroll: true,
      onError: () => toast.error('Failed to send email'),
    })
  }

  function handleDiscard() {
    if (confirm('Discard this draft?')) {
      router.post(pathFor(`/emails/${draft.id}/discard`), queryParams, {
        preserveScroll: true,
        onError: () => toast.error('Failed to discard draft'),
      })
    }
  }

  function handleEditComplete() {
    setIsEditing(false)
  }

  // Provenance badge text
  const provenanceText = isAIDraft
    ? 'AI draft'
    : draft.created_by_name
      ? `Operator draft — ${draft.created_by_name}`
      : 'Operator draft'

  if (isEditing) {
    const editHeader = (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isAIDraft ? <Bot className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-muted-foreground" />}
          <span className="text-[13px] font-semibold text-foreground">{provenanceText}</span>
          <Badge variant="secondary" size="sm" className="font-medium">
            {capitalize(draft.status)}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">{formatShortDateTime(draft.created_at)}</span>
      </div>
    )

    return (
      <DraftEditor
        draft={draft}
        to={draft.to}
        cc={draft.cc}
        bcc={draft.bcc}
        header={editHeader}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        filter={filter}
        attention={attention}
        search={search}
        cursor={cursor}
        onClose={handleEditComplete}
      />
    )
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.02] p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isAIDraft ? <Bot className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-muted-foreground" />}
          <span className="text-[13px] font-semibold text-foreground">{provenanceText}</span>
          {isAIDraft && draft.agent_session_id && (
            <Link href={pathFor(`/agent_sessions/${draft.agent_session_id}`)} className="text-xs text-primary hover:underline cursor-pointer">
              View session
            </Link>
          )}
          <Badge variant="secondary" size="sm" className="font-medium">
            {capitalize(draft.status)}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">{formatShortDateTime(draft.created_at)}</span>
      </div>

      <div className="text-xs text-muted-foreground">
        To: {draft.to}
        {draft.cc && <> &middot; CC: {draft.cc}</>}
        {draft.bcc && <> &middot; BCC: {draft.bcc}</>}
      </div>

      <div className="text-[13px] leading-[1.6] text-foreground whitespace-pre-wrap">
        {displayBody}
      </div>

      {/* Show original toggle for edited AI drafts */}
      {isAIDraft && isEdited && (
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
        >
          {showOriginal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showOriginal ? 'Show edited version' : 'Show original'}
        </button>
      )}

      {/* Send error display */}
      {isSendFailed && draft.send_error && (
        <p className="text-xs text-red-600">
          Send failed: {draft.send_error}
        </p>
      )}

      {/* Sent confirmation */}
      {draft.status === 'sent' && draft.sent_at && (
        <p className="text-xs text-green-600">
          Sent on {formatShortDateTime(draft.sent_at)}
        </p>
      )}

      {/* Attachments (read-only in view mode — use Edit to add/remove) */}
      {attachments.length > 0 && (
        <AttachmentPicker
          emailId={draft.id}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          disabled={true}
        />
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        {canDiscard && (
          <Button variant="outline" size="sm" className="h-7 text-xs cursor-pointer" onClick={handleDiscard}>
            Discard
          </Button>
        )}
        {canEdit && (
          <Button variant="outline" size="sm" className="h-7 text-xs cursor-pointer" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        {canSend && (
          <Button size="sm" className="h-7 text-xs cursor-pointer" onClick={handleSend}>
            {isSendFailed ? 'Retry Send' : 'Send'}
          </Button>
        )}
      </div>
    </div>
  )
}
